package ui.components

import ThemeState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun Navbar(
    onNavigateHome: (() -> Unit)? = null,
    onNavigateToAboutUs: (() -> Unit)? = null,
    onNavigateToExplore: (() -> Unit)? = null,
    onNavigateToNews: (() -> Unit)? = null,
    onDownloadClick: (() -> Unit)? = null
) {
    var isMenuOpen by remember { mutableStateOf(false) }
    var isDropdownOpen by remember { mutableStateOf(false) }
    
    // CONTROL PRO: Estado reactivo del ancho de ventana
    var windowWidth by remember { mutableStateOf(window.innerWidth) }
    
    // Función para cambiar de tema
    val toggleTheme: () -> Unit = {
        ThemeState.value = !ThemeState.value
        if (ThemeState.value) {
            document.documentElement?.classList?.add("dark")
            localStorage.setItem("drrecipe_theme", "dark")
        } else {
            document.documentElement?.classList?.remove("dark")
            localStorage.setItem("drrecipe_theme", "light")
        }
    }
    
    LaunchedEffect(Unit) {
        window.onresize = {
            windowWidth = window.innerWidth
            if (windowWidth >= 1024) isMenuOpen = false
            null
        }
    }

    val isDesktop = windowWidth >= 1024

    Nav(attrs = {
        classes("fixed", "top-0", "left-0", "right-0", "z-50", "bg-white/80", "dark:bg-medical-surfaceDark/80", "backdrop-blur-md", "border-b", "border-gray-100", "dark:border-gray-800")
    }) {
        Div(attrs = {
            classes("max-w-7xl", "mx-auto", "px-4", "sm:px-6", "lg:px-8")
        }) {
            Div(attrs = {
                classes("flex", "justify-between", "items-center", "h-20")
            }) {
                // 1. Logo
                A(href = if (onNavigateHome != null) "javascript:void(0)" else "#inicio", attrs = {
                    classes("flex", "items-center", "gap-4", "cursor-pointer", "no-underline", "group")
                    if (onNavigateHome != null) onClick { onNavigateHome() }
                    else onClick { isMenuOpen = false }
                }) {
                    Div(attrs = {
                        classes("bg-white", "dark:bg-medical-bgDark", "p-1", "rounded-xl", "shadow-sm", "border", "border-gray-100", "dark:border-gray-800", "group-hover:shadow-md", "transition-shadow")
                    }) {
                        Img(src = "dr_recipe_solo.svg", attrs = { classes("h-11", "w-auto") })
                    }
                    H2(attrs = { classes("text-2xl", "font-bold", "text-medical-primary", "dark:text-medical-primaryDark") }) { Text("Dr. Recipe") }
                }

                // 2. Renderizado Condicional: Desktop vs Mobile
                if (isDesktop) {
                    Div(attrs = {
                        classes("flex", "items-center", "space-x-1")
                    }) {
                        if (onNavigateHome == null) {
                            NavLink(href = "#funciones", text = "Funciones")
                            NavLink(href = "#proceso", text = "Proceso")
                            NavLink(href = "#testimonios", text = "Testimonios")
                            NavLink(href = "#preguntas", text = "FAQ")
                            NavLink(href = "#precios", text = "Precios")

                            Div(attrs = {
                                classes("relative", "ml-2")
                                onMouseEnter { isDropdownOpen = true }
                                onMouseLeave { isDropdownOpen = false }
                            }) {
                                Div(attrs = {
                                    classes("flex", "items-center", "gap-1", "text-gray-600", "dark:text-gray-300", "hover:text-medical-primary", "dark:hover:text-medical-primaryDark", "font-medium", "px-3", "py-2", "transition-colors", "cursor-pointer")
                                    onClick { isDropdownOpen = !isDropdownOpen }
                                }) {
                                    Text("Conócenos")
                                    Span(attrs = { 
                                        classes("material-symbols-outlined", "text-sm", "transition-transform", "mt-0.5") 
                                        if (isDropdownOpen) style { property("transform", "rotate(180deg)") }
                                    }) { Text("expand_more") }
                                }

                                if (isDropdownOpen) {
                                    Div(attrs = {
                                        classes("absolute", "top-full", "right-0", "pt-2", "w-56", "z-[100]")
                                    }) {
                                        Div(attrs = {
                                            classes("bg-white", "dark:bg-medical-surfaceDark", "rounded-2xl", "shadow-2xl", "border", "border-gray-100", "dark:border-medical-surfaceVariantDark", "py-2", "overflow-hidden")
                                        }) {
                                            DropdownItem(text = "Explora App", icon = "smartphone", onClick = { isDropdownOpen = false; onNavigateToExplore?.invoke() })
                                            Div(attrs = { classes("h-px", "bg-gray-100", "dark:bg-gray-700", "my-2", "mx-4") })
                                            DropdownItem(text = "Seguridad", icon = "check_circle", onClick = { 
                                                isDropdownOpen = false
                                                if (window.location.hash == "#seguridad") {
                                                    // Si ya está en el hash, forzamos un pequeño scroll o reasignación
                                                    val el = document.getElementById("seguridad")
                                                    el?.scrollIntoView()
                                                } else {
                                                    window.location.hash = "seguridad" 
                                                }
                                            })
                                        }
                                    }
                                }
                            }

                            // --- Botón de Tema (Sol/Luna) Desktop ---
                            Button(attrs = {
                                classes("ml-2", "p-2", "rounded-full", "text-gray-600", "dark:text-gray-300", "hover:bg-gray-100", "dark:hover:bg-gray-800", "transition-colors", "flex", "items-center", "justify-center", "w-10", "h-10")
                                onClick { toggleTheme() }
                            }) {
                                Span(attrs = { classes("material-symbols-outlined", "text-xl", "leading-none") }) {
                                    Text(if (ThemeState.value) "light_mode" else "dark_mode")
                                }
                            }

                            M3OutlinedButton(attrs = {
                                classes("ml-3")
                                onClick {
                                    if (onDownloadClick != null) onDownloadClick()
                                    else window.open("https://play.google.com/store/apps/details?id=com.artisancode.drrecipe", "_blank")
                                }
                            }) { Text("Descargar App") }
                        } else {
                            // --- Botón de Tema (Sol/Luna) Desktop (Páginas Secundarias) ---
                            Button(attrs = {
                                classes("mr-4", "p-2", "rounded-full", "text-gray-600", "dark:text-gray-300", "hover:bg-gray-100", "dark:hover:bg-gray-800", "transition-colors", "flex", "items-center", "justify-center", "w-10", "h-10")
                                onClick { toggleTheme() }
                            }) {
                                Span(attrs = { classes("material-symbols-outlined", "text-xl", "leading-none") }) {
                                    Text(if (ThemeState.value) "light_mode" else "dark_mode")
                                }
                            }
                            M3FilledButton(attrs = { classes("ml-6"); onClick { onNavigateHome() } }) { Text("Inicio") }
                        }
                    }
                } else {
                    Div(attrs = { classes("flex", "items-center", "gap-2") }) {
                        // --- Botón de Tema (Sol/Luna) Mobile ---
                        Button(attrs = {
                            classes("p-2", "rounded-xl", "text-gray-600", "dark:text-gray-300", "hover:bg-gray-100", "dark:hover:bg-gray-800", "transition-colors", "flex", "items-center", "justify-center", "w-10", "h-10")
                            onClick { toggleTheme() }
                        }) {
                            Span(attrs = { classes("material-symbols-outlined", "text-2xl", "leading-none") }) {
                                Text(if (ThemeState.value) "light_mode" else "dark_mode")
                            }
                        }
                        
                        Button(attrs = {
                            classes("p-2", "rounded-xl", "text-gray-600", "dark:text-gray-300", "hover:bg-gray-100", "dark:hover:bg-gray-800", "transition-colors")
                            onClick { isMenuOpen = !isMenuOpen }
                        }) {
                            Span(attrs = { classes("material-symbols-outlined", "text-3xl") }) { Text(if (isMenuOpen) "close" else "menu") }
                        }
                    }
                }
            }
        }

        if (!isDesktop && isMenuOpen) {
            Div(attrs = {
                classes("bg-white", "dark:bg-medical-surfaceDark", "border-b", "border-gray-100", "dark:border-gray-800", "px-4", "pt-2", "pb-6", "space-y-1")
            }) {
                if (onNavigateHome == null) {
                    MobileNavLink(href = "#funciones", text = "Funciones", onClick = { isMenuOpen = false })
                    MobileNavLink(href = "#proceso", text = "Proceso", onClick = { isMenuOpen = false })
                    MobileNavLink(href = "#testimonios", text = "Testimonios", onClick = { isMenuOpen = false })
                    MobileNavLink(href = "#preguntas", text = "FAQ", onClick = { isMenuOpen = false })
                    MobileNavLink(href = "#precios", text = "Precios", onClick = { isMenuOpen = false })
                    
                    Div(attrs = { classes("h-px", "bg-gray-100", "my-4") })
                    
                    MobileNavLink(href = "javascript:void(0)", text = "Explora App", onClick = { isMenuOpen = false; onNavigateToExplore?.invoke() })
                    MobileNavLink(href = "javascript:void(0)", text = "Seguridad", onClick = { 
                        isMenuOpen = false
                        if (window.location.hash == "#seguridad") {
                            document.getElementById("seguridad")?.scrollIntoView()
                        } else {
                            window.location.hash = "seguridad" 
                        }
                    })
                }
                
                Div(attrs = { classes("pt-6", "flex", "flex-col", "gap-3") }) {
                    if (onNavigateHome == null) {
                        M3OutlinedButton(attrs = { classes("w-full"); onClick { isMenuOpen = false; window.location.hash = "seguridad" } }) { Text("Seguridad e Info") }
                        M3FilledButton(attrs = {
                            classes("w-full")
                            onClick {
                                isMenuOpen = false
                                if (onDownloadClick != null) onDownloadClick()
                                else window.open("https://play.google.com/store/apps/details?id=com.artisancode.drrecipe", "_blank")
                            }
                        }) { Text("Descargar App") }
                    } else {
                        M3FilledButton(attrs = { classes("w-full"); onClick { isMenuOpen = false; onNavigateHome() } }) { Text("Inicio") }
                    }
                }
            }
        }
    }
}

@Composable
private fun DropdownItem(text: String, icon: String, onClick: () -> Unit) {
    Div(attrs = {
        classes("flex", "items-center", "gap-3", "px-4", "py-3", "hover:bg-medical-primary/5", "dark:hover:bg-medical-primaryDark/10", "cursor-pointer", "transition-colors", "group")
        onClick { onClick() }
    }) {
        Span(attrs = { classes("material-symbols-outlined", "text-gray-400", "dark:text-gray-500", "group-hover:text-medical-primary", "dark:group-hover:text-medical-primaryDark", "text-lg") }) { Text(icon) }
        Span(attrs = { classes("text-sm", "font-medium", "text-gray-600", "dark:text-gray-300", "group-hover:text-medical-primary", "dark:group-hover:text-medical-primaryDark") }) { Text(text) }
    }
}

@Composable
private fun NavLink(href: String, text: String, onClick: (() -> Unit)? = null) {
    A(href = href, attrs = {
        classes("text-gray-600", "dark:text-gray-300", "hover:text-medical-primary", "dark:hover:text-medical-primaryDark", "font-medium", "px-3", "py-2", "transition-colors", "no-underline")
        if (onClick != null) onClick { onClick() }
    }) {
        Text(text)
    }
}

@Composable
private fun MobileNavLink(href: String, text: String, onClick: () -> Unit) {
    A(href = href, attrs = {
        classes("block", "text-gray-600", "dark:text-gray-300", "hover:text-medical-primary", "dark:hover:text-medical-primaryDark", "font-medium", "px-3", "py-3", "rounded-xl", "hover:bg-medical-primary/5", "dark:hover:bg-medical-primaryDark/10", "no-underline")
        onClick { onClick() }
    }) {
        Text(text)
    }
}
